import { cn } from '@/common/utils/cn'
import { formatCurrency, formatDecimalUnit } from '@/common/utils/formatters'
import {
  GovernmentAllocationSummaryData,
  GovernmentAllocationSummaryTableData,
  ProductAllocationSummaryProductData,
} from '@/features/governmentAllocations/types/governmentAllocationSummary.type'
import { ChevronRight } from 'lucide-react'

const emptyCell = <p></p>
const dashCell = <p>-</p>

const createCurrencyRow = (
  categoryName: JSX.Element | string,
  productName: JSX.Element | string,
  data: {
    allocated_total: string
    distributed_total: string
    available_total: string
    consumed_total: string
    remaining_distribution_total: string
  },
  isCategory: boolean,
  handleToggleCategory?: (categoryId: string) => void,
  categoriesIsOpen?: Record<string, boolean>,
  categoryId?: string
) => ({
  _rowClassName: isCategory ? 'bg-white-100' : 'bg-blue-100',
  actions: isCategory ? (
    <ChevronRight
      className={cn(
        'cursor-pointer',
        categoryId && categoriesIsOpen?.[categoryId] && 'rotate-90'
      )}
      onClick={() => categoryId && handleToggleCategory?.(categoryId)}
    />
  ) : emptyCell,
  category_display_name: categoryName,
  product_display_name: productName,
  allocated_total: !!data.allocated_total ? formatCurrency(Number(data.allocated_total)) : dashCell,
  distributed_total: !!data.distributed_total ? (
    <p className="text-yellow-400">{formatCurrency(Number(data.distributed_total))}</p>
  ) : (
    dashCell
  ),
  available_total: !!data.available_total ? (
    <p className="text-valid">{formatCurrency(Number(data.available_total))}</p>
  ) : (
    dashCell
  ),
  consumed_total: !!data.consumed_total ? (
    <p className="text-yellow-400">{formatCurrency(Number(data.consumed_total))}</p>
  ) : (
    dashCell
  ),
  remaining_distribution_total: !!data.remaining_distribution_total ? (
    <p className="text-valid">{formatCurrency(Number(data.remaining_distribution_total))}</p>
  ) : (
    dashCell
  ),
})

const createQuantityRow = (
  categoryName: JSX.Element | string,
  productName: JSX.Element | string,
  data: {
    allocated_quantity: string
    distributed_quantity: string
    available_quantity: string
    consumed_quantity: string
    remaining_distribution_quantity: string
    unit_for_quantity: string
  },
  isCategory: boolean,
  handleToggleCategory?: (categoryId: string) => void,
  categoriesIsOpen?: Record<string, boolean>,
  categoryId?: string
) => ({
  _rowClassName: isCategory ? 'bg-white-100' : 'bg-blue-100',
   actions: isCategory ? (
    <ChevronRight
      className={cn(
        'cursor-pointer',
        categoryId && categoriesIsOpen?.[categoryId] && 'rotate-90'
      )}
      onClick={() => categoryId && handleToggleCategory?.(categoryId)}
    />
  ) : emptyCell,
  category_display_name: categoryName,
  product_display_name: productName,
  allocated_quantity: !!data.allocated_quantity
    ? formatDecimalUnit(data.allocated_quantity, data.unit_for_quantity)
    : dashCell,
  distributed_quantity: !!data.distributed_quantity ? (
    <p className="text-yellow-400">{formatDecimalUnit(data.distributed_quantity, data.unit_for_quantity)}</p>
  ) : (
    dashCell
  ),
  available_quantity: !!data.available_quantity ? (
    <p className="text-valid">{formatDecimalUnit(data.available_quantity, data.unit_for_quantity)}</p>
  ) : (
    dashCell
  ),
  consumed_quantity: !!data.consumed_quantity ? (
    <p className="text-yellow-400">{formatDecimalUnit(data.consumed_quantity, data.unit_for_quantity)}</p>
  ) : (
    dashCell
  ),
  remaining_distribution_quantity: !!data.remaining_distribution_quantity ? (
    <p className="text-valid">{formatDecimalUnit(data.remaining_distribution_quantity, data.unit_for_quantity)}</p>
  ) : (
    dashCell
  ),
})

const createEmptyRow = (isCurrency: boolean) => {
  return isCurrency
    ? {
        _rowClassName: 'bg-white-100',
        category_display_name: emptyCell,
        product_display_name: emptyCell,
        allocated_total: dashCell,
        distributed_total: dashCell,
        available_total: dashCell,
        consumed_total: dashCell,
        remaining_distribution_total: dashCell,
      }
    : {
        _rowClassName: 'bg-white-100',
        category_display_name: emptyCell,
        product_display_name: emptyCell,
        allocated_quantity: dashCell,
        distributed_quantity: dashCell,
        available_quantity: dashCell,
        consumed_quantity: dashCell,
        remaining_distribution_quantity: dashCell,
      }
}

export class GovernmentAllocationSummaryTransformer {
  static tableTransformer(
    controlledBy: string,
    categoriesIsOpen: {[key: string]: boolean},
    handleToggleCategory: (categoryId: string) => void
  ): (governmentAllocationSummary: GovernmentAllocationSummaryData) => GovernmentAllocationSummaryTableData[] {
    return (governmentAllocationSummary: GovernmentAllocationSummaryData) => {
      const tableData: GovernmentAllocationSummaryTableData[] = []

      const { categories, products } = governmentAllocationSummary

      const isCurrency = controlledBy === 'currency'

      const processedProductIds = new Set<string>()

      Object.entries(categories ?? {}).forEach(([categoryId, category]) => {
        // Add category row
        if (isCurrency) {
          tableData.push(createCurrencyRow(
            category.display_name,
            emptyCell,
            category,
            true,
            handleToggleCategory,
            categoriesIsOpen,
            categoryId
          ))
        } else {
          const firstUnit = Object.values(products).find(
            (product) => product.category_id === categoryId,
          )?.unit_for_quantity

          tableData.push(
            createQuantityRow(
              category.display_name,
              emptyCell,
              { ...category, unit_for_quantity: firstUnit || '' },
              true,
              handleToggleCategory,
              categoriesIsOpen,
              categoryId
            ),
          )
        }

        // Find and add products belonging to this category
        Object.entries(products ?? {}).forEach(([productId, product]) => {
          if (product.category_id === categoryId) {
            processedProductIds.add(productId)

            if(categoriesIsOpen[product.category_id] === true) {
              if (isCurrency) {
                tableData.push(createCurrencyRow(product.display_name, emptyCell, product, false))
              } else {
                tableData.push(createQuantityRow(product.display_name, emptyCell, product, false))
              }
            }
          }
        })
      })

      // Group orphan products (products with category_id but no matching category)
      const orphanProductsByCategory: Record<string, ProductAllocationSummaryProductData[]> = {}

      Object.entries(products ?? {}).forEach(([productId, product]) => {
        if (!processedProductIds.has(productId)) {
          if (!orphanProductsByCategory[product.category_id]) {
            orphanProductsByCategory[product.category_id] = []
          }
          orphanProductsByCategory[product.category_id].push(product)
        }
      })

      // Add orphan products with fake category
      Object.values(orphanProductsByCategory).forEach((orphanProducts) => {
        // Add fake category row
        tableData.push(createEmptyRow(isCurrency))

        // Add orphan products
        orphanProducts.forEach((product) => {
          if (isCurrency) {
            tableData.push(createCurrencyRow('', product.display_name, product, false))
          } else {
            tableData.push(createQuantityRow('', product.display_name, product, false))
          }
        })
      })

      return tableData
    }
  }
}
